import { useEffect, useMemo } from 'react';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import {
	BigNum,
	BN,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import useAppInitialLoad from 'src/hooks/app/useAppInitialLoad';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { AccountData } from 'src/stores/useDriftAccountsStore';
import { useAllAccountsMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import { MAIN_POOL_ID, UIMarket } from '@drift/common';
import { useAllIfData } from 'src/hooks/query/ifData/useAllIfData';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { IFStakeItem } from 'src/components/Drawers/AccountsDrawer/accounts/types';
import { DRIFT_TOKEN_BANK_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { UiVaults } from 'src/constants/vaults';
import { getAuthorityVaultBalance } from 'src/utils/vault/math';
import useSyncAccountRent from 'src/hooks/useSyncAccountRent';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';

export default function usePortfolioAccountsData() {
	const accounts = useTickedAllSubAccounts();
	const { isInitialLoading } = useAppInitialLoad();
	const isWalletConnected = useWalletIsConnected();

	useSyncAccountRent();

	const { data: allMarginInfos } = useAllAccountsMarginInfo();

	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const { data: vaultStatsData } = useAllVaultStats();
	const vaultStatsLookup = vaultStatsData.lookup;
	const setVaultDisplaySettings = useVaultsStore(
		(s) => s.setVaultDisplaySettings
	);

	// For some ungodly reason this is needed to fetch external vault stats.
	useEffect(() => {
		setVaultDisplaySettings((draft) => {
			draft.targetVaultManager = 'All';
		});
	}, [setVaultDisplaySettings]);

	const { data: vaultDepositorLookup } = useVaultDepositorAccounts();
	const vaultsWithBalance = Object.values(vaultDepositorLookup ?? {}).filter(
		(vaultAccount) => vaultAccount.vaultDepositor.vaultShares.gt(new BN(0))
	);

	const vaultDepositorAccounts = useMemo(() => {
		return Object.values(vaultDepositorLookup ?? {}).map(
			(vault) => vault.vaultDepositor
		);
	}, [vaultDepositorLookup]);

	const { data: ifDataLookup } = useAllIfData();

	const ifStakesWithBalance = useMemo(() => {
		return Object.entries(ifDataLookup)
			.filter(([_, ifData]) => {
				const currentStake = ifData.userStake.currentStake ?? 0;
				return currentStake > 0;
			})
			.map(([marketIndex, ifData]): IFStakeItem => {
				const market = UIMarket.createSpotMarket(Number(marketIndex));
				return {
					marketIndex: Number(marketIndex),
					ifData,
					poolId: (market.market as SpotMarketConfig).poolId,
					market,
				};
			});
	}, [ifDataLookup]);

	const { driftStake, nonDriftIfStake } = useMemo(() => {
		const drift: IFStakeItem[] = [];
		const nonDrift: IFStakeItem[] = [];

		ifStakesWithBalance.forEach((item) => {
			if (item.marketIndex === DRIFT_TOKEN_BANK_INDEX) {
				drift.push(item);
			} else {
				nonDrift.push(item);
			}
		});

		return { driftStake: drift, nonDriftIfStake: nonDrift };
	}, [ifStakesWithBalance]);

	const { mainPoolAccounts, isolatedPoolAccounts, delegatedAccounts } =
		useMemo(() => {
			const mainPool: AccountData[] = [];
			const isolatedPool: AccountData[] = [];
			const delegated: AccountData[] = [];

			for (const account of accounts) {
				if (account.isDelegatedTo) {
					delegated.push(account);
				} else if (account.poolId === MAIN_POOL_ID) {
					mainPool.push(account);
				} else {
					isolatedPool.push(account);
				}
			}

			return {
				mainPoolAccounts: mainPool,
				isolatedPoolAccounts: isolatedPool,
				delegatedAccounts: delegated,
			};
		}, [accounts]);

	const {
		totalValue,
		isolatedPoolTotalValue,
		mainPoolTotalValue,
		delegatedTotalValue,
		vaultTotalValue,
		driftStakeTotalValue,
		nonDriftStakeTotalValue,
	} = useMemo(() => {
		if (!isWalletConnected || !accounts.length) {
			return {};
		}

		const mainPoolTotalValue = mainPoolAccounts.reduce((acc, accountData) => {
			const mi = allMarginInfos?.[accountData.userKey];
			return acc.add(mi?.netUsdValue ?? new BN(0));
		}, new BN(0));

		const isolatedPoolTotalValue = isolatedPoolAccounts.reduce(
			(acc, accountData) => {
				const mi = allMarginInfos?.[accountData.userKey];
				return acc.add(mi?.netUsdValue ?? new BN(0));
			},
			new BN(0)
		);

		const delegatedTotalValue = delegatedAccounts.reduce((acc, accountData) => {
			const mi = allMarginInfos?.[accountData.userKey];
			return acc.add(mi?.netUsdValue ?? new BN(0));
		}, new BN(0));

		const vaultTotalValue = vaultsWithBalance.reduce((acc, vaultAccount) => {
			const vaultPubkey = vaultAccount.vault.pubkey.toString();
			const vaultStat = vaultStatsLookup[vaultPubkey];
			const uiVaultConfig = UiVaults.getVaultConfig(vaultPubkey);

			if (!vaultStat || !uiVaultConfig) {
				return acc;
			}

			const balance = getAuthorityVaultBalance(
				[vaultAccount.vaultDepositor],
				{ ...vaultStat, uiVaultConfig },
				'quote',
				getOraclePriceForMarket
			);

			return acc.add(balance);
		}, BigNum.zero(QUOTE_PRECISION_EXP));

		const driftStakeTotalValue = driftStake.reduce((acc, accountData) => {
			return acc.add(
				accountData.ifData.userStake.currentStakeNotionalValue ??
					BigNum.zero(QUOTE_PRECISION_EXP)
			);
		}, BigNum.zero(QUOTE_PRECISION_EXP));

		const nonDriftStakeTotalValue = nonDriftIfStake.reduce(
			(acc, accountData) => {
				return acc.add(
					accountData.ifData.userStake.currentStakeNotionalValue ??
						BigNum.zero(QUOTE_PRECISION_EXP)
				);
			},
			BigNum.zero(QUOTE_PRECISION_EXP)
		);

		const mainPoolTotalValueBigNum = BigNum.from(
			mainPoolTotalValue,
			QUOTE_PRECISION_EXP
		);

		const isolatedPoolTotalValueBigNum = BigNum.from(
			isolatedPoolTotalValue,
			QUOTE_PRECISION_EXP
		);

		return {
			totalValue: mainPoolTotalValueBigNum
				.add(isolatedPoolTotalValueBigNum)
				.add(vaultTotalValue)
				.add(driftStakeTotalValue)
				.add(nonDriftStakeTotalValue),
			mainPoolTotalValue: mainPoolTotalValueBigNum,
			delegatedTotalValue: BigNum.from(
				delegatedTotalValue,
				QUOTE_PRECISION_EXP
			),
			isolatedPoolTotalValue: isolatedPoolTotalValueBigNum,
			vaultTotalValue,
			driftStakeTotalValue,
			nonDriftStakeTotalValue,
		};
	}, [
		accounts,
		allMarginInfos,
		vaultStatsLookup,
		getOraclePriceForMarket,
		vaultsWithBalance,
		driftStake,
		nonDriftIfStake,
		isInitialLoading,
		isWalletConnected,
		mainPoolAccounts,
		isolatedPoolAccounts,
		delegatedAccounts,
	]);

	return {
		totalValue,
		delegatedTotalValue,
		mainPoolTotalValue,
		isolatedPoolTotalValue,
		vaultTotalValue,
		nonDriftStakeTotalValue,
		driftStakeTotalValue,
		vaultsWithBalance,
		nonDriftIfStake,
		driftStake,
		mainPoolAccounts,
		delegatedAccounts,
		isolatedPoolAccounts,
		vaultDepositorAccounts,
	};
}
